import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import postSalonDataAndRefreshTokens from 'actions/salon/postSalonDataAndRefreshTokens.action';
import generateDefaultSalonData from 'bl/salon/generateDefaultSalonData.bl';
import generateQuickSetupConfig from 'bl/salon/generateQuickSetupConfig.bl';
import recalculateQuickAppointmentHours from 'bl/salon/recalculateQuickAppointmentHours.bl';
import validateEmail from 'bl/validation/validateEmail.bl';
import validateName from 'bl/validation/validateName.bl';
import CheckboxInput from 'components/form/CheckboxInput.component';
import EnumeratedInput from 'components/form/EnumeratedInput.component';
import FormField from 'components/form/FormField.component';
import IntegerInput from 'components/form/IntegerInput.component';
import TextInput from 'components/form/TextInput.component';
import SalonSetupLocationFields from 'components/onboarding/SalonSetupLocationFields.component';
import CASING from 'constants/casing.const';
import LINK from 'constants/link.const';
import BUSINESS_NAME from 'constants/salon/businessName.const';
import S from 'constants/string.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import {
  selectOnboardingCompleted,
  selectOnboardingError,
  selectOnboardingIsPosting,
  selectOnboardingResponse,
} from 'selectors/onboarding.selectors';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import {
  selectCognitoAttributeEmail,
  selectCognitoAttributeName,
  selectCognitoAttributePhone,
  selectCognitoUserObject,
} from 'utils/cognito.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromObjectToQueryString from 'utils/converters/object/fromObjectToQueryString.util';
import { fromPropsToQuery, fromPropsToQueryString } from 'utils/converters/props/path.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import batchValidate from 'utils/predicates/batchValidate.util';
import isNotFn from 'utils/predicates/function/isNotFn.util';
import isFalsy from 'utils/predicates/isFalsy.util';
import isFullString from 'utils/predicates/string/isFullString.util';
import finishOnboarding from 'utils/salons/finishOnboarding.util';
import byText from 'utils/sorters/byText.util';

import PopupModalContainer from 'common/PopupModal';
import { normalizeEmail } from 'lib/normalize';
import Header from 'onboarding/common/Header';
import OnboardingFooter from 'onboarding/common/OnboardingFooter';

import CN from './SalonSetupPage.module.scss';

const CTAG = 'SalonSetupPage';
const L = logger(CTAG);

const RENDER_LOREAL = false;
const USE_BUSY_TYPE_DROPDOWN = false;

const DEFAULT_USER_NAME = 'New Best Friend';
const DEFAULT_SALON_NAME = 'Your Salon';

const MANAGE_BOOKINGS = Object.freeze(
  [
    'Paper diary',
    'Shortcuts',
    'iSalon',
    'SalonIQ',
    'Phorest',
    'Ovatu',
    'Timely',
    'Vagaro',
    'Kitomba',
    'Fresha',
    'Booksy',
    'Treatwell',
    'Appointd',
    'Acuity',
    'Zenoti',
    'Day Smart',
    'Salon Advantage',
    'The Salon App',
    'Squire',
    'Nearcut',
    'Ikosoft',
  ].sort(byText({ casing: CASING.ignore })),
);

const F = Object.freeze({
  name: 'name',
  phon: 'phone',
  mail: 'email',
  empl: 'numberOfEmployees',
  city: 'city',
  adl1: 'address_line_1',
  adl2: 'address_line_2',
  post: 'postcode',
  prty: 'pricing_type',
  lore: 'loreal',
  busi: 'business_type',
  size: 'size',
  book: 'manage_booking_method',
});

const MESSAGE = Object.freeze({
  [F.name]: 'Please enter a salon name',
  [F.mail]: 'Please enter a valid email',
  [F.busi]: 'Please select a business type',
  [F.book]: 'Please select a provider',
  [F.adl1]: 'Please enter address line 1',
  [F.city]: 'Please enter a town or city',
  [F.post]: 'Please enter a postcode',
});

const VALIDATOR = Object.freeze({
  [F.name]: validateName,
  [F.mail]: validateEmail,
  [F.size]: ({ value }) => Number.isSafeInteger(value) && value > 0,
  // FIXME: keys and label are mixed up
  // [F.busi]: ({ value }) => isEnumVal(BUSINESS_TYPE, value),
  [F.book]: ({ value }) => isFullString(value),
  [F.adl1]: ({ value }) => isFullString(fromAnyToTrimmed(value)),
  [F.city]: ({ value }) => isFullString(fromAnyToTrimmed(value)),
  [F.post]: ({ value }) => isFullString(fromAnyToTrimmed(value)),
});

const determineQuickSetupType = (props) =>
  getAnyOrVoid('location.state.quickSetupType', props) || fromPropsToQuery(props).quickSetupType;

const SalonSetupPage = (props) => {
  const {
    cognitoName,
    cognitoPhone,
    cognitoUser,
    isPosting,
    onboardingError,
    onboardingResponse,
    postSalonDataAndRefreshTokens,
    quickSalonCreationCompleted: isQuickCompleted,
    cognitoEmail,
  } = props;

  const nav = navFor(CTAG, props);
  const query = fromPropsToQuery(props);
  const queryString = fromPropsToQueryString(props);
  const isQuickSetup = fromAnyToBool(determineQuickSetupType(props));

  const isNormalFlow = getAnyOrVoid('location.state.normalFlow', props) || query.normalFlow === 'true';

  const [errorMessage, setErrorMessage] = useStateWhileMounted(onboardingError);
  const [fields, setFields] = useStateWhileMounted(
    generateDefaultSalonData({ phone: cognitoPhone, email: cognitoEmail }),
  );
  const [locationData, setLocationData] = useState({ country: '', timezone: '' });
  const { val: triedToSubmit, yay: showFooterSubmit, nay: hideFooterSubmit } = useTip(false);

  const isBatchValidated = batchValidate({ validators: VALIDATOR, fields });
  const isLocationValid = isFullString(locationData?.country);
  const isNextEnabled = fromAnyToBool(!isPosting) && isBatchValidated && isLocationValid;

  const salonId = getAnyOrVoid('response.id', onboardingResponse);
  const salonName = fromAnyToTrimmed(fields[F.name]) || DEFAULT_SALON_NAME;
  const userName = fromAnyToTrimmed(cognitoName) || DEFAULT_USER_NAME;
  const namePlaceholder = `${userName}\`s Salon`;

  const handleDismiss = useCallback(() => void setErrorMessage(S.empty), [setErrorMessage]);
  const handleGoBack = useCallback(
    //
    () => void nav.push({ pathname: LINK.onboardingType, search: queryString }),
    [nav, queryString],
  );

  const handleChange = useCallback(
    (ev) => {
      const { name: fieldName, raw, number } = asObject(ev);

      // little massaging of the input values to get the desired type or canonical representation
      let v = raw;
      if (fieldName === F.mail) v = normalizeEmail(raw);
      if (fieldName === F.size) v = number;

      if (fields[fieldName] === v) return;

      setFields({ ...fields, [fieldName]: v });
    },
    [fields, setFields],
  );

  const handleValidate = useCallback((ev) => {
    const { name: fieldName, dirty, raw: value } = asObject(ev);
    const validate = VALIDATOR[fieldName];

    // no point validating if there wasn't even a single change
    if (!dirty) return null;
    if (isNotFn(validate)) return null;

    // return null to clear the error from before
    return { error: validate({ value }) ? null : MESSAGE[fieldName] };
  }, []);

  const handleSubmit = () => {
    if (!isNextEnabled) return;

    // NOTE: only quick setups are allowed atm
    const quickSetupType = determineQuickSetupType(props);
    if (!quickSetupType) return;

    const options = {
      quick: true,
      type: quickSetupType,
      data: recalculateQuickAppointmentHours({
        ...generateQuickSetupConfig({ type: quickSetupType }),
        ...fields,
        country: locationData?.country, // TODO: BE to resolves object and hardcoded/default data
        timezone: locationData?.timezone,
      }),
    };

    L.unignore('handleSubmit()', options, postSalonDataAndRefreshTokens(options)).then(async (result) => {
      await finishOnboarding({ cognitoUser });

      const salonId = L.debug('handleSubmit()', 'salonId:', getAnyOrVoid('response.id', result));
      nav.push({ search: fromObjectToQueryString({ ...query, salonId }) });
    });
  };

  const submitSalon = () => {
    if (!isNextEnabled) {
      showFooterSubmit();
      return;
    }

    hideFooterSubmit();
    handleSubmit();
  };

  // from componentDidMount, it scrolled the window to focus on the input form
  useEffect(() => void window.scrollTo(0, 0), []);

  // from componentWillMount, was used to redirected if this step is already finished/unnecessary
  useEffect(() => void (!isNormalFlow && nav.push(LINK.onboardingType)), [isNormalFlow, nav]);

  // from willReceiveProps, was used to skip this step if already completed
  useEffect(() => {
    if (!isQuickCompleted) return;

    nav.push({
      pathname: LINK.onboardingType,
      state: {
        loadSalon: onboardingResponse,
        redirectTo: { pathname: LINK.onboardingStylists, state: { quick: true } },
      },
      search: fromObjectToQueryString({
        ...query,
        quick: true,
        salonId,
        redirectTo: LINK.onboardingStylists,
      }),
    });
  }, [isQuickCompleted, nav, onboardingResponse, query, salonId]);

  L.debug('()', { isNextEnabled });

  return (
    <div data-bem="SalonSetupPage" className={CN.component}>
      {errorMessage && (
        <PopupModalContainer
          popupTitle="Error"
          popupMessage={errorMessage}
          buttonText="Okay"
          buttonCallback={handleDismiss}
        />
      )}

      <Header
        titleFirstPart="OK, "
        userName={userName}
        titleSecondPart=". Let's Learn About "
        salonName={salonName}
        titleThirdPart="."
        description="Add some information about your business."
        backBtnCallBack={handleGoBack}
      />

      <div data-bem="SalonSetupPageV2__content" className={CN.content}>
        <div className={CN.constraint}>
          <FormField
            input={TextInput}
            name={F.name}
            value={fields[F.name]}
            label="Business Name"
            placeholder={namePlaceholder}
            onChange={handleChange}
            onValidate={handleValidate}
          />

          <FormField
            input={TextInput}
            name={F.mail}
            value={fields[F.mail]}
            label="Business Email Address"
            placeholder="lola@missjackson.com"
            onChange={handleChange}
            onValidate={handleValidate}
          />

          <FormField
            input={TextInput}
            name={F.adl1}
            value={fields[F.adl1]}
            label="Address Line 1"
            placeholder="Address Line 1"
            onChange={handleChange}
            onValidate={handleValidate}
          />

          <FormField
            input={TextInput}
            name={F.adl2}
            value={fields[F.adl2]}
            label="Address Line 2"
            placeholder="Address Line 2"
            onChange={handleChange}
            onValidate={handleValidate}
          />

          <FormField
            input={TextInput}
            name={F.city}
            value={fields[F.city]}
            label="Town / City"
            placeholder="Town / City"
            onChange={handleChange}
            onValidate={handleValidate}
          />

          <FormField
            input={TextInput}
            name={F.post}
            value={fields[F.post]}
            label="Postcode"
            placeholder="W1S 1DW"
            onChange={handleChange}
            onValidate={handleValidate}
          />

          <SalonSetupLocationFields onChange={setLocationData} />

          {isFalsy(isQuickSetup) && (
            <FormField
              input={IntegerInput}
              name={F.empl}
              value={fields[F.empl]}
              label="Number of Stylists"
              placeholder=""
              onChange={handleChange}
              onValidate={handleValidate}
            />
          )}

          <FormField
            input={IntegerInput}
            name={F.size}
            value={fields[F.size]}
            label="Number of Bookable Staff"
            onChange={handleChange}
            onValidate={handleValidate}
          />

          {USE_BUSY_TYPE_DROPDOWN && (
            <FormField
              input={EnumeratedInput}
              label="Business Type"
              placeholder={MESSAGE[F.busi]}
              name={F.busi}
              value={fields[F.busi]}
              options={BUSINESS_NAME}
              onChange={handleChange}
              onValidate={handleValidate}
            />
          )}

          <FormField
            input={EnumeratedInput}
            label="Manage Bookings"
            placeholder={MESSAGE[F.book]}
            name={F.book}
            value={fields[F.book]}
            options={MANAGE_BOOKINGS}
            onChange={handleChange}
            onValidate={handleValidate}
          />

          {RENDER_LOREAL && (
            <FormField
              input={CheckboxInput}
              name={F.lore}
              value={fields[F.lore]}
              label="We use L'Oréal products"
              onChange={handleChange}
              onValidate={handleValidate}
            />
          )}
        </div>

        {triedToSubmit && !isNextEnabled && <div className={CN.error}>Please complete all fields to continue</div>}
      </div>

      <OnboardingFooter isFooterButtonActive={isNextEnabled} onButtonClick={submitSalon} isLoading={isPosting} />

      {isPosting && <div data-bem="SalonSetupPageV2__load-blocker" className={CN.loadBlocker} />}
    </div>
  );
};

const mapStateToProps = (state) => ({
  cognitoUser: selectCognitoUserObject(state),
  cognitoName: selectCognitoAttributeName(state),
  cognitoPhone: selectCognitoAttributePhone(state),
  cognitoEmail: selectCognitoAttributeEmail(state),
  isPosting: selectOnboardingIsPosting(state),
  quickSalonCreationCompleted: selectOnboardingCompleted(state),
  onboardingError: selectOnboardingError(state),
  onboardingResponse: selectOnboardingResponse(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ postSalonDataAndRefreshTokens }, dispatch);

// this extra export is needed for tests that provide their own mock for connect()
export { SalonSetupPage as SalonSetupPage4Test };

const SalonSetupPageV2Hoc = connect(mapStateToProps, mapDispatchToProps)(SalonSetupPage);

export default SalonSetupPageV2Hoc;
